import headshot from "./images/profile_3.jpeg";
import "./App.css";

const offerings = [
  {
    id: "01",
    title: "AI & automation",
    text: "The invoices, the inbox, the spreadsheet someone updates by hand every Monday. The hours spent manually inputting data from emails into systems. We find the repetitive work and build around it, with AI where it earns its place and a human check where it matters.",
    items: ["AI assistants & internal tools", "Document & data workflows", "Business system integrations"],
  },
  {
    id: "02",
    title: "Custom software",
    text: "Web and mobile applications shaped around how your business actually runs, from the first prototype to the version your customers rely on.",
    items: ["Web & mobile applications", "APIs & integrations", "Full-stack development"],
  },
  {
    id: "03",
    title: "Product & UX",
    text: "Before building, we work out what’s worth building. Research, interface design and the product decisions that keep a project small and useful.",
    items: ["Research & product strategy", "Experience & interface design", "Analytics & iteration"],
  },
];

const steps = [
  { id: "01 / UNDERSTAND", title: "Find the friction", text: "We conduct deep interviews to understand your business before writing code." },
  { id: "02 / BUILD", title: "Connect the pieces", text: "Your existing systems, traditional automation tools, plus AI where it actually helps." },
  { id: "03 / ITERATE", title: "Measure and refine", text: "Quantify your results and tweak the system as needed." },
];

const email = "mailto:hey@bithaiku.com?subject=Let%27s%20work%20together";

function Arrow() {
  return <span aria-hidden="true">↗</span>;
}

function InArrow() {
  return <span aria-hidden="true">→</span>;
}

export default function App() {
  return (
    <div className="site" id="top">
      <a className="skip-link" href="#main">Skip to content</a>

      <div className="c-top">
        <header className="c-header c-container">
          <a className="c-brand" href="#top" aria-label="Bit Haiku home">bit haiku<span>.</span></a>
          <nav aria-label="Main navigation">
            <a href="#services">Services</a>
            <a href="#about">About</a>
            <a href="#contact">Let’s talk <InArrow /></a>
          </nav>
        </header>
      </div>

      <main id="main">
        <div className="c-hero-wrap">
          <section className="c-hero c-container" aria-labelledby="hero-title">
            <div className="systems-intro">
              <p className="c-label">[ SOFTWARE / AI AUTOMATION / SECURITY ]</p>
              <h1 id="hero-title">MORE BUSINESS.<br /><span>LESS BUSYWORK.</span></h1>
              <p className="c-lead">Bit Haiku is a small studio that builds custom software and AI automations for companies with better things to do than repetitive work.</p>
              <a className="c-button" href="#contact">Take the first step <InArrow /></a>
            </div>
            <aside className="systems-flow" aria-label="How a project goes">
              <div className="flow-header"><span>HOW A PROJECT GOES</span><span aria-hidden="true">↘</span></div>
              <ol>
                {steps.map((step) => (
                  <li key={step.id}>
                    <span>{step.id}</span>
                    <h3>{step.title}</h3>
                    <p>{step.text}</p>
                  </li>
                ))}
              </ol>
            </aside>
          </section>
        </div>

        <section className="c-services c-container" id="services" aria-labelledby="services-title">
          <div className="c-section-head">
            <p className="c-label">01 / WHAT WE DO</p>
            <h2 id="services-title">REMOVE THE PAIN<br />FROM YOUR PROCESSES.</h2>
          </div>
          <div className="c-offerings">
            {offerings.map((offering) => (
              <article key={offering.id} className="c-offering">
                <span className="c-index">{offering.id}</span>
                <h3>{offering.title}</h3>
                <p>{offering.text}</p>
                <ul>{offering.items.map((item) => <li key={item}>{item}</li>)}</ul>
              </article>
            ))}
          </div>
        </section>

        <section className="c-belief" aria-labelledby="belief-title">
          <div className="c-container c-belief-inner">
            <p className="c-label">02 / HOW WE WORK</p>
            <h2 id="belief-title">PURPOSE BEFORE TECHNOLOGY.</h2>
            <div>
              <p>Every project starts with your business, not a tech stack. We look for the bottleneck, simplify what surrounds it, and build the smallest thing that removes it.</p>
              <p>We work closely with your team and ship code your next developer can read. Throughout our work, we keep security a top priority, not an afterthought.</p>
              <p>We’ve helped small and large companies in medical devices, financial services, manufacturing, and logistics.</p>
            </div>
          </div>
        </section>

        <section className="c-about c-container" id="about" aria-labelledby="about-title">
          <div className="c-founder-image">
            <img src={headshot} alt="Tamas Erdos, founder of Bit Haiku" width="440" height="480" loading="lazy" />
            <p className="c-label">TAMAS ERDOS / FOUNDER</p>
          </div>
          <div className="c-founder-copy">
            <p className="c-label">03 / WHO YOU’LL WORK WITH</p>
            <h2 id="about-title">HI, I’M TAMAS.</h2>
            <p>I founded Bit Haiku to help businesses turn complex challenges into simple, useful solutions.</p>
            <p>I’ve led innovation teams at startups, mid-sized businesses, and Fortune 500 companies. Now I lead a small team of developers and focus on getting the big picture right: product, process, and strategy.</p>
            <a className="c-inline-link" href="#contact">Let’s talk about how you can move faster <InArrow /></a>
          </div>
        </section>

        <section className="c-contact" id="contact" aria-labelledby="contact-title">
          <div className="c-container">
            <p className="c-label">04 / GET IN TOUCH</p>
            <h2 id="contact-title">WHAT WOULD<br />SIMPLICITY<br />FEEL LIKE?</h2>
            <div className="c-contact-row">
              <p>A product idea. A process that takes too long.<br />A question about whether AI could help.<br />Send it over, even if it’s half-formed.</p>
              <a href={email}>hey@bithaiku.com <Arrow /></a>
            </div>
          </div>
        </section>
      </main>

      <footer className="c-footer c-container">
        <a className="c-brand" href="#top">bit haiku.</a>
        <span>© {new Date().getFullYear()} Bit Haiku LLC</span>
        <div>
          <a href="https://github.com/erdostom" target="_blank" rel="noreferrer">GitHub <Arrow /></a>
          <a href="https://twitter.com/tamas" target="_blank" rel="noreferrer">Twitter <Arrow /></a>
          <a href="https://www.iubenda.com/privacy-policy/44877060" target="_blank" rel="noreferrer">Privacy</a>
        </div>
      </footer>
    </div>
  );
}
